export interface HUDWeaponState {
  name: string;
  ammo: number;
  reserve: number;
  status?: string;
  heat?: number;
  rifleCooldown?: number;
  missileCooldown?: number;
  bladeCooldown?: number;
}

export interface HUDTargetState {
  name: string;
  distance: number;
  armor: number;
  maxArmor: number;
  stagger: number;
  maxStagger: number;
  locked: boolean;
  /** True while the target's ACS is overloaded — the ×1.65 window. */
  overloaded?: boolean;
  /** Command units escalate to phase 2; the panel badges it persistently. */
  phase?: 1 | 2;
  threat?: string;
  screenX?: number;
  screenY?: number;
}

export type DamageTier = "graze" | "solid" | "heavy" | "severe";
export type HitMarkerKind = "hit" | "direct" | "kill";

export interface HUDObjectiveState {
  title: string;
  detail?: string;
  /** Normalized objective completion, from 0 to 1. */
  progress?: number;
}

export interface HUDState {
  armor: number;
  maxArmor: number;
  energy: number;
  maxEnergy: number;
  stagger: number;
  maxStagger: number;
  weapon: HUDWeaponState;
  missiles: number;
  /** Normalized lock acquisition, from 0 to 1. */
  lockProgress: number;
  target: HUDTargetState | null;
  objective: HUDObjectiveState;
  elapsedSeconds?: number;
}

export interface HUDResult {
  victory: boolean;
  title?: string;
  summary?: string;
  score?: number;
  elapsedSeconds?: number;
}

export type HUDToastTone = "info" | "warning" | "success";

interface HUDElements {
  startOverlay: HTMLElement;
  startButton: HTMLButtonElement;
  pauseOverlay: HTMLElement;
  resumeButton: HTMLButtonElement;
  resultOverlay: HTMLElement;
  resultKicker: HTMLElement;
  resultTitle: HTMLElement;
  resultSummary: HTMLElement;
  resultScore: HTMLElement;
  resultTime: HTMLElement;
  restartButton: HTMLButtonElement;
  apBar: HTMLElement;
  apFill: HTMLElement;
  apValue: HTMLElement;
  enBar: HTMLElement;
  enFill: HTMLElement;
  enValue: HTMLElement;
  staggerBar: HTMLElement;
  staggerFill: HTMLElement;
  staggerValue: HTMLElement;
  weaponName: HTMLElement;
  weaponAmmo: HTMLElement;
  weaponReserve: HTMLElement;
  weaponStatus: HTMLElement;
  weaponHeat: HTMLElement;
  rifleCooldown: HTMLElement;
  missileCooldown: HTMLElement;
  bladeCooldown: HTMLElement;
  missileCount: HTMLElement;
  reticle: HTMLElement;
  lockRing: HTMLElement;
  lockLabel: HTMLElement;
  targetPanel: HTMLElement;
  targetName: HTMLElement;
  targetDistance: HTMLElement;
  targetThreat: HTMLElement;
  targetIntegrity: HTMLElement;
  targetFill: HTMLElement;
  targetStagger: HTMLElement;
  targetStaggerFill: HTMLElement;
  objectiveTitle: HTMLElement;
  objectiveDetail: HTMLElement;
  objectiveProgress: HTMLElement;
  missionClock: HTMLElement;
  toast: HTMLElement;
  damageLayer: HTMLElement;
  hitWedge: HTMLElement;
  boostLayer: HTMLElement;
  damageNumberLayer: HTMLElement;
  apDelta: HTMLElement;
  hitMarker: HTMLElement;
  targetStatus: HTMLElement;
  targetPhase: HTMLElement;
}

interface DamageCounter {
  element: HTMLElement;
  total: number;
  lastHitAt: number;
  expireTimer: number | null;
}

const clamp = (value: number, min = 0, max = 1): number => {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
};

const ratio = (value: number, maximum: number): number => {
  if (!Number.isFinite(maximum) || maximum <= 0) return 0;
  return clamp(value / maximum);
};

const displayInteger = (value: number): string =>
  Math.max(0, Math.round(Number.isFinite(value) ? value : 0)).toLocaleString("en-US");

const displayTime = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(Number.isFinite(totalSeconds) ? totalSeconds : 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * DOM-based tactical HUD. It owns only presentation and delegates game actions
 * to callbacks supplied by the game controller.
 */
export class HUD {
  private root: HTMLElement | null = null;
  private elements: HUDElements | null = null;
  private startHandler: (() => void) | null = null;
  private resumeHandler: (() => void) | null = null;
  private restartHandler: (() => void) | null = null;
  private toastTimer: number | null = null;
  private damageTimer: number | null = null;
  private hitTimer: number | null = null;
  private hitMarkerTimer: number | null = null;
  private apDeltaTimer: number | null = null;
  private breakTimer: number | null = null;
  /** One live cumulative damage counter per target id. */
  private readonly damageCounters = new Map<string, DamageCounter>();
  private readonly damagePool: HTMLElement[] = [];
  /** While frozen (test capture), animated feedback is held rather than
   * expired, so a screenshot taken seconds later still shows the moment. */
  private frozen = false;

  public mount(
    container: HTMLElement = document.getElementById("hud-root") ?? document.body,
  ): HTMLElement {
    if (this.root?.parentElement === container) return this.root;
    this.disposeDom();

    const root = document.createElement("div");
    root.className = "game-hud";
    root.dataset.testid = "game-hud";
    root.innerHTML = this.template();
    container.append(root);

    const elements: HUDElements = {
      startOverlay: this.required(root, '[data-testid="start-overlay"]'),
      startButton: this.required(root, '[data-testid="start-button"]'),
      pauseOverlay: this.required(root, '[data-testid="pause-overlay"]'),
      resumeButton: this.required(root, '[data-testid="resume-button"]'),
      resultOverlay: this.required(root, '[data-testid="result-overlay"]'),
      resultKicker: this.required(root, '[data-ui="result-kicker"]'),
      resultTitle: this.required(root, '[data-ui="result-title"]'),
      resultSummary: this.required(root, '[data-ui="result-summary"]'),
      resultScore: this.required(root, '[data-ui="result-score"]'),
      resultTime: this.required(root, '[data-ui="result-time"]'),
      restartButton: this.required(root, '[data-testid="restart-button"]'),
      apBar: this.required(root, '[data-testid="ap-bar"]'),
      apFill: this.required(root, '[data-ui="ap-fill"]'),
      apValue: this.required(root, '[data-ui="ap-value"]'),
      enBar: this.required(root, '[data-testid="en-bar"]'),
      enFill: this.required(root, '[data-ui="en-fill"]'),
      enValue: this.required(root, '[data-ui="en-value"]'),
      staggerBar: this.required(root, '[data-testid="stagger-bar"]'),
      staggerFill: this.required(root, '[data-ui="stagger-fill"]'),
      staggerValue: this.required(root, '[data-ui="stagger-value"]'),
      weaponName: this.required(root, '[data-ui="weapon-name"]'),
      weaponAmmo: this.required(root, '[data-ui="weapon-ammo"]'),
      weaponReserve: this.required(root, '[data-ui="weapon-reserve"]'),
      weaponStatus: this.required(root, '[data-ui="weapon-status"]'),
      weaponHeat: this.required(root, '[data-ui="weapon-heat"]'),
      rifleCooldown: this.required(root, '[data-ui="rifle-cooldown"]'),
      missileCooldown: this.required(root, '[data-ui="missile-cooldown"]'),
      bladeCooldown: this.required(root, '[data-ui="blade-cooldown"]'),
      missileCount: this.required(root, '[data-ui="missile-count"]'),
      reticle: this.required(root, '[data-testid="reticle"]'),
      lockRing: this.required(root, '[data-testid="lock-ring"]'),
      lockLabel: this.required(root, '[data-ui="lock-label"]'),
      targetPanel: this.required(root, '[data-testid="target-panel"]'),
      targetName: this.required(root, '[data-ui="target-name"]'),
      targetDistance: this.required(root, '[data-ui="target-distance"]'),
      targetThreat: this.required(root, '[data-ui="target-threat"]'),
      targetIntegrity: this.required(root, '[data-ui="target-integrity"]'),
      targetFill: this.required(root, '[data-ui="target-fill"]'),
      targetStagger: this.required(root, '[data-ui="target-stagger"]'),
      targetStaggerFill: this.required(root, '[data-ui="target-stagger-fill"]'),
      objectiveTitle: this.required(root, '[data-ui="objective-title"]'),
      objectiveDetail: this.required(root, '[data-ui="objective-detail"]'),
      objectiveProgress: this.required(root, '[data-ui="objective-progress"]'),
      missionClock: this.required(root, '[data-ui="mission-clock"]'),
      toast: this.required(root, '[data-testid="toast"]'),
      damageLayer: this.required(root, '[data-testid="damage-layer"]'),
      hitWedge: this.required(root, '[data-ui="hit-wedge"]'),
      boostLayer: this.required(root, '[data-testid="boost-layer"]'),
      damageNumberLayer: this.required(root, '[data-testid="damage-number-layer"]'),
      apDelta: this.required(root, '[data-ui="ap-delta"]'),
      hitMarker: this.required(root, '[data-ui="hit-marker"]'),
      targetStatus: this.required(root, '[data-ui="target-status"]'),
      targetPhase: this.required(root, '[data-ui="target-phase"]'),
    };

    elements.startButton.addEventListener("click", this.handleStartClick);
    elements.resumeButton.addEventListener("click", this.handleResumeClick);
    elements.restartButton.addEventListener("click", this.handleRestartClick);
    this.root = root;
    this.elements = elements;
    return root;
  }

  public update(state: HUDState): void {
    const elements = this.elements;
    if (!elements) return;

    this.updateMeter(
      elements.apBar,
      elements.apFill,
      elements.apValue,
      state.armor,
      state.maxArmor,
      "AP",
      true,
    );
    this.updateMeter(
      elements.enBar,
      elements.enFill,
      elements.enValue,
      state.energy,
      state.maxEnergy,
      "EN",
    );
    this.updateMeter(
      elements.staggerBar,
      elements.staggerFill,
      elements.staggerValue,
      state.stagger,
      state.maxStagger,
      "ACS",
      false,
      true,
    );

    const weapon = state.weapon;
    elements.weaponName.textContent = weapon.name || "UNARMED";
    elements.weaponAmmo.textContent = displayInteger(weapon.ammo);
    elements.weaponReserve.textContent = `/ ${displayInteger(weapon.reserve)}`;
    elements.weaponStatus.textContent = weapon.status?.toUpperCase() || "READY";
    elements.weaponStatus.dataset.state =
      weapon.ammo <= 0 ? "empty" : weapon.status?.toLowerCase() || "ready";
    const heat = clamp(weapon.heat ?? 0);
    elements.weaponHeat.style.setProperty("--heat", `${heat * 100}%`);
    elements.weaponHeat.setAttribute("aria-valuenow", Math.round(heat * 100).toString());
    this.updateCooldown(elements.rifleCooldown, weapon.rifleCooldown ?? 0);
    this.updateCooldown(elements.missileCooldown, weapon.missileCooldown ?? 0);
    this.updateCooldown(elements.bladeCooldown, weapon.bladeCooldown ?? 0);
    elements.missileCount.textContent = displayInteger(state.missiles);

    const lock = state.target?.locked ? 1 : clamp(state.lockProgress);
    elements.lockRing.style.setProperty("--lock-angle", `${lock * 360}deg`);
    elements.lockRing.classList.toggle("is-acquiring", lock > 0 && lock < 1);
    elements.lockRing.classList.toggle("is-locked", lock >= 1);
    elements.reticle.classList.toggle("is-locked", lock >= 1);
    elements.lockLabel.textContent = lock >= 1 ? "LOCK" : lock > 0 ? `${Math.round(lock * 100)}%` : "";

    this.updateTarget(state.target);
    elements.objectiveTitle.textContent = state.objective.title || "NO ACTIVE DIRECTIVE";
    elements.objectiveDetail.textContent = state.objective.detail ?? "";
    const objectiveProgress = clamp(state.objective.progress ?? 0);
    elements.objectiveProgress.style.setProperty("--objective", `${objectiveProgress * 100}%`);
    elements.objectiveProgress.hidden = state.objective.progress === undefined;
    elements.missionClock.textContent = displayTime(state.elapsedSeconds ?? 0);
  }

  /** Shows the deployment gate. Passing false hides it without changing callbacks. */
  public showStart(onStart?: (() => void) | boolean): void {
    const elements = this.elements;
    if (!elements) return;
    if (typeof onStart === "function") this.startHandler = onStart;
    if (onStart === false) {
      elements.startOverlay.hidden = true;
      return;
    }
    elements.startOverlay.hidden = false;
    elements.startButton.focus({ preventScroll: true });
  }

  public hideStart(): void {
    if (this.elements) this.elements.startOverlay.hidden = true;
  }

  public showPause(onResume?: () => void): void {
    const elements = this.elements;
    if (!elements) return;
    this.resumeHandler = onResume ?? this.resumeHandler;
    elements.pauseOverlay.hidden = false;
    elements.resumeButton.focus({ preventScroll: true });
  }

  public hidePause(): void {
    if (this.elements) this.elements.pauseOverlay.hidden = true;
  }

  public showResult(result: HUDResult, onRestart?: () => void): void {
    const elements = this.elements;
    if (!elements) return;
    this.restartHandler = onRestart ?? null;
    elements.resultOverlay.dataset.result = result.victory ? "victory" : "failure";
    elements.resultKicker.textContent = result.victory ? "PROTOCOL COMPLETE" : "SIGNAL TERMINATED";
    elements.resultTitle.textContent =
      result.title ?? (result.victory ? "VEIL SECURED" : "FRAME DESTROYED");
    elements.resultSummary.textContent =
      result.summary ??
      (result.victory
        ? "All hostile command signatures have been neutralized."
        : "Combat telemetry archived. Re-entry is available.");
    elements.resultScore.textContent = displayInteger(result.score ?? 0);
    elements.resultTime.textContent = displayTime(result.elapsedSeconds ?? 0);
    elements.resultOverlay.hidden = false;
    elements.restartButton.focus({ preventScroll: true });
  }

  public hideResult(): void {
    if (this.elements) this.elements.resultOverlay.hidden = true;
  }

  public setToast(message: string, tone: HUDToastTone = "info", duration = 2200): void {
    const toast = this.elements?.toast;
    if (!toast) return;
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    toast.textContent = message;
    toast.dataset.tone = tone;
    toast.classList.toggle("is-visible", message.length > 0);
    this.toastTimer = null;
    if (message.length > 0 && duration > 0) {
      this.toastTimer = window.setTimeout(() => {
        toast.classList.remove("is-visible");
        this.toastTimer = null;
      }, duration);
    }
  }

  /**
   * Floating damage read-out at the hit point. Rapid fire folds into ONE
   * counter per target that grows and re-anchors on each hit — a stream of
   * rifle rounds reads as "1,425 and climbing", while a blade lands as a
   * single huge figure. Tier classes size and colour the number so 285 and
   * 2,887 are unmistakably different objects on screen.
   */
  public spawnDamage(
    screenX: number,
    screenY: number,
    total: number,
    tier: DamageTier,
    direct: boolean,
    targetKey: string,
  ): void {
    const layer = this.elements?.damageNumberLayer;
    if (!layer) return;

    let counter = this.damageCounters.get(targetKey) ?? null;
    if (!counter) {
      const element = this.damagePool.pop() ?? document.createElement("span");
      element.className = "hud__dmg";
      layer.append(element);
      counter = { element, total: 0, lastHitAt: 0, expireTimer: null };
      this.damageCounters.set(targetKey, counter);
    }
    counter.total = total;
    counter.lastHitAt = performance.now();
    const element = counter.element;
    element.textContent = displayInteger(total);
    element.dataset.tier = tier;
    element.classList.toggle("is-direct", direct);
    element.style.left = `${clamp(screenX, 0.05, 0.95) * 100}%`;
    element.style.top = `${clamp(screenY, 0.08, 0.9) * 100}%`;
    // Restart the pop animation from frame zero for every hit.
    element.classList.remove("is-live");
    void element.offsetWidth;
    element.classList.add("is-live");

    if (counter.expireTimer !== null) window.clearTimeout(counter.expireTimer);
    counter.expireTimer = window.setTimeout(() => this.expireDamageCounter(targetKey), 950);
  }

  private expireDamageCounter(targetKey: string): void {
    const counter = this.damageCounters.get(targetKey);
    if (!counter) return;
    if (this.frozen) {
      // Hold the read-out while a capture freeze is on; re-check later.
      counter.expireTimer = window.setTimeout(() => this.expireDamageCounter(targetKey), 500);
      return;
    }
    counter.element.classList.remove("is-live");
    counter.element.remove();
    this.damagePool.push(counter.element);
    this.damageCounters.delete(targetKey);
  }

  /** Reticle tick on a landed hit; escalates for direct hits and kills. */
  public showHitMarker(kind: HitMarkerKind): void {
    const marker = this.elements?.hitMarker;
    if (!marker) return;
    if (this.hitMarkerTimer !== null) window.clearTimeout(this.hitMarkerTimer);
    marker.dataset.kind = kind;
    marker.classList.remove("is-live");
    void marker.offsetWidth;
    marker.classList.add("is-live");
    this.hitMarkerTimer = window.setTimeout(() => {
      if (this.frozen) return;
      marker.classList.remove("is-live");
      this.hitMarkerTimer = null;
    }, kind === "kill" ? 420 : 240);
  }

  /**
   * Incoming damage figure beside the AP meter: "what did that just cost me",
   * which no amount of vignette can carry on its own.
   */
  public showIncomingDamage(amount: number, direct: boolean): void {
    const readout = this.elements?.apDelta;
    if (!readout) return;
    if (this.apDeltaTimer !== null) window.clearTimeout(this.apDeltaTimer);
    readout.textContent = `-${displayInteger(amount)}`;
    readout.classList.toggle("is-direct", direct);
    readout.classList.remove("is-live");
    void readout.offsetWidth;
    readout.classList.add("is-live");
    this.apDeltaTimer = window.setTimeout(() => {
      if (this.frozen) return;
      readout.classList.remove("is-live");
      this.apDeltaTimer = null;
    }, 1100);
  }

  /** One-shot white flash across the target panel at the break instant. */
  public flashStaggerBreak(): void {
    const panel = this.elements?.targetPanel;
    if (!panel) return;
    if (this.breakTimer !== null) window.clearTimeout(this.breakTimer);
    panel.classList.remove("is-stagger-break");
    void panel.offsetWidth;
    panel.classList.add("is-stagger-break");
    this.breakTimer = window.setTimeout(() => {
      if (this.frozen) return;
      panel.classList.remove("is-stagger-break");
      this.breakTimer = null;
    }, 900);
  }

  /**
   * Capture freeze: pauses CSS animations (via a root class) and keeps
   * timer-driven feedback alive until thawed. Test-mode only in practice; it
   * exists so a 1 fps software renderer can still photograph "the moment".
   */
  public setFrozen(frozen: boolean): void {
    this.frozen = frozen;
    this.root?.classList.toggle("is-fx-frozen", frozen);
  }

  public pulseDamage(intensity = 1): void {
    const layer = this.elements?.damageLayer;
    if (!layer) return;
    if (this.damageTimer !== null) window.clearTimeout(this.damageTimer);
    layer.style.setProperty("--damage-intensity", clamp(intensity, 0.15, 1).toString());
    layer.classList.remove("is-active");
    void layer.offsetWidth;
    layer.classList.add("is-active");
    this.damageTimer = window.setTimeout(() => this.expireDamagePulse(), 420);
  }

  private expireDamagePulse(): void {
    if (this.frozen) {
      // Capture freeze: hold the flash instead of expiring it mid-photograph.
      this.damageTimer = window.setTimeout(() => this.expireDamagePulse(), 500);
      return;
    }
    this.elements?.damageLayer.classList.remove("is-active");
    this.damageTimer = null;
  }

  /**
   * Swing the damage arc to point at whatever just hit us.
   *
   * `yaw` is in mech-local radians with 0 straight ahead. The chase camera sits
   * behind the mech, so local forward is screen-up and the yaw maps onto a CSS
   * rotation unchanged.
   */
  public markHitDirection(yaw: number): void {
    const wedge = this.elements?.hitWedge;
    if (!wedge || !Number.isFinite(yaw)) return;
    if (this.hitTimer !== null) window.clearTimeout(this.hitTimer);
    wedge.style.setProperty("--hit-yaw", `${yaw}rad`);
    wedge.classList.remove("is-active");
    void wedge.offsetWidth;
    wedge.classList.add("is-active");
    this.hitTimer = window.setTimeout(() => this.expireHitWedge(), 760);
  }

  private expireHitWedge(): void {
    if (this.frozen) {
      this.hitTimer = window.setTimeout(() => this.expireHitWedge(), 500);
      return;
    }
    this.elements?.hitWedge.classList.remove("is-active");
    this.hitTimer = null;
  }

  public setBoost(active: boolean, assault = false): void {
    this.elements?.boostLayer.classList.toggle("is-active", active);
    this.elements?.boostLayer.classList.toggle("is-assault", assault);
    this.root?.classList.toggle("is-boosting", active);
    this.root?.classList.toggle("is-assault-boosting", assault);
  }

  public dispose(): void {
    this.disposeDom();
    this.startHandler = null;
    this.resumeHandler = null;
    this.restartHandler = null;
  }

  private readonly handleStartClick = (): void => {
    this.hideStart();
    this.startHandler?.();
  };

  private readonly handleRestartClick = (): void => {
    this.hideResult();
    this.restartHandler?.();
  };

  private readonly handleResumeClick = (): void => {
    this.hidePause();
    this.resumeHandler?.();
  };

  private updateTarget(target: HUDTargetState | null): void {
    const elements = this.elements;
    if (!elements) return;
    if (!target) {
      elements.targetPanel.hidden = true;
      elements.reticle.classList.remove("has-target");
      elements.reticle.style.left = "50%";
      elements.reticle.style.top = "50%";
      return;
    }

    elements.targetPanel.hidden = false;
    elements.reticle.classList.add("has-target");
    elements.targetName.textContent = target.name || "UNKNOWN CONTACT";
    elements.targetDistance.textContent = `${displayInteger(target.distance)} m`;
    elements.targetThreat.textContent = target.threat?.toUpperCase() || "HOSTILE";
    elements.targetIntegrity.textContent = `${displayInteger(target.armor)} / ${displayInteger(
      target.maxArmor,
    )}`;
    const integrity = ratio(target.armor, target.maxArmor);
    elements.targetFill.style.setProperty("--target-integrity", `${integrity * 100}%`);
    const overloaded = target.overloaded === true;
    elements.targetStagger.textContent = overloaded
      ? "OVERLOAD"
      : `${displayInteger(target.stagger)} / ${displayInteger(target.maxStagger)}`;
    const stagger = overloaded ? 1 : ratio(target.stagger, target.maxStagger);
    elements.targetStaggerFill.style.setProperty("--target-stagger", `${stagger * 100}%`);
    elements.targetPanel.classList.toggle("is-locked", target.locked);
    elements.targetPanel.classList.toggle("is-critical", integrity <= 0.25);
    // The three ACS states are three different panels: filling (bar climbs and
    // hums as it nears the top), OVERLOAD (badge + amber bar, the ×1.65
    // window), recovering (the same bar draining back down).
    elements.targetPanel.classList.toggle("is-overloaded", overloaded);
    elements.targetPanel.classList.toggle("is-priming", !overloaded && stagger >= 0.7);
    elements.targetStatus.hidden = !overloaded;
    const phaseTwo = target.phase === 2;
    elements.targetPhase.hidden = !phaseTwo;
    elements.reticle.classList.toggle("is-overloaded", overloaded);
    if (target.screenX !== undefined && target.screenY !== undefined) {
      elements.reticle.style.left = `${clamp(target.screenX, 0.08, 0.92) * 100}%`;
      elements.reticle.style.top = `${clamp(target.screenY, 0.12, 0.86) * 100}%`;
    }
  }

  private updateCooldown(element: HTMLElement, cooldown: number): void {
    const normalized = clamp(cooldown);
    element.style.setProperty("--cooldown", `${normalized * 100}%`);
    element.dataset.ready = normalized <= 0 ? "true" : "false";
    element.setAttribute("aria-valuenow", Math.round((1 - normalized) * 100).toString());
  }

  private updateMeter(
    meter: HTMLElement,
    fill: HTMLElement,
    valueLabel: HTMLElement,
    value: number,
    maximum: number,
    name: string,
    lowIsDanger = false,
    highIsDanger = false,
  ): void {
    const normalized = ratio(value, maximum);
    fill.style.setProperty("--meter", `${normalized * 100}%`);
    valueLabel.textContent = `${displayInteger(value)} / ${displayInteger(maximum)}`;
    meter.setAttribute("aria-valuenow", Math.round(clamp(value, 0, maximum)).toString());
    meter.setAttribute("aria-valuemax", Math.max(0, maximum).toString());
    meter.setAttribute("aria-valuetext", `${name} ${displayInteger(value)} of ${displayInteger(maximum)}`);
    meter.classList.toggle("is-warning", (lowIsDanger && normalized <= 0.3) || (highIsDanger && normalized >= 0.72));
    meter.classList.toggle("is-critical", (lowIsDanger && normalized <= 0.15) || (highIsDanger && normalized >= 0.92));
  }

  private disposeDom(): void {
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    if (this.damageTimer !== null) window.clearTimeout(this.damageTimer);
    if (this.hitTimer !== null) window.clearTimeout(this.hitTimer);
    if (this.hitMarkerTimer !== null) window.clearTimeout(this.hitMarkerTimer);
    if (this.apDeltaTimer !== null) window.clearTimeout(this.apDeltaTimer);
    if (this.breakTimer !== null) window.clearTimeout(this.breakTimer);
    for (const counter of this.damageCounters.values()) {
      if (counter.expireTimer !== null) window.clearTimeout(counter.expireTimer);
    }
    this.damageCounters.clear();
    this.damagePool.length = 0;
    this.toastTimer = null;
    this.damageTimer = null;
    this.hitTimer = null;
    this.hitMarkerTimer = null;
    this.apDeltaTimer = null;
    this.breakTimer = null;
    if (this.elements) {
      this.elements.startButton.removeEventListener("click", this.handleStartClick);
      this.elements.resumeButton.removeEventListener("click", this.handleResumeClick);
      this.elements.restartButton.removeEventListener("click", this.handleRestartClick);
    }
    this.root?.remove();
    this.root = null;
    this.elements = null;
  }

  private required<T extends Element>(root: ParentNode, selector: string): T {
    const element = root.querySelector<T>(selector);
    if (!element) throw new Error(`HUD element not found: ${selector}`);
    return element;
  }

  private template(): string {
    return `
      <div class="hud__vignette" data-testid="vignette-layer" aria-hidden="true"></div>
      <div class="hud__damage" data-testid="damage-layer" aria-hidden="true"></div>
      <div class="hud__hit" data-testid="hit-direction" aria-hidden="true">
        <span class="hud__hit-wedge" data-ui="hit-wedge"></span>
      </div>
      <div class="hud__boost" data-testid="boost-layer" aria-hidden="true">
        <span></span><span></span><span></span><span></span>
      </div>
      <div class="hud__dmg-layer" data-testid="damage-number-layer" aria-hidden="true"></div>

      <header class="hud__top-rail" aria-label="Mission status">
        <div class="hud__identity">
          <span class="hud__identity-mark" aria-hidden="true">IV</span>
          <span><strong>IRON VEIL</strong><small>PROTOCOL // FRAME 07</small></span>
        </div>
        <section class="hud__objective" aria-label="Current objective">
          <span class="hud__section-tag">DIRECTIVE</span>
          <strong data-ui="objective-title">INITIALIZING...</strong>
          <small data-ui="objective-detail"></small>
          <span class="hud__objective-progress" data-ui="objective-progress" aria-hidden="true"></span>
        </section>
        <div class="hud__clock">
          <span>OP TIME</span>
          <time data-ui="mission-clock">00:00</time>
        </div>
      </header>

      <section class="hud__meters hud-panel" aria-label="Frame systems">
        <div class="hud__meter hud__meter--armor" data-testid="ap-bar" role="progressbar" aria-label="Armor points" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100">
          <div class="hud__meter-heading"><span><b>AP</b> ARMOR</span><strong data-ui="ap-value">100 / 100</strong></div>
          <div class="hud__meter-track"><span data-ui="ap-fill"></span></div>
          <span class="hud__ap-delta" data-ui="ap-delta" aria-hidden="true"></span>
        </div>
        <div class="hud__meter hud__meter--energy" data-testid="en-bar" role="progressbar" aria-label="Energy" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100">
          <div class="hud__meter-heading"><span><b>EN</b> DRIVE</span><strong data-ui="en-value">100 / 100</strong></div>
          <div class="hud__meter-track"><span data-ui="en-fill"></span></div>
        </div>
        <div class="hud__meter hud__meter--stagger" data-testid="stagger-bar" role="progressbar" aria-label="Attitude control strain" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
          <div class="hud__meter-heading"><span><b>ACS</b> STRAIN</span><strong data-ui="stagger-value">0 / 100</strong></div>
          <div class="hud__meter-track"><span data-ui="stagger-fill"></span></div>
        </div>
      </section>

      <section class="hud__weapon hud-panel" aria-label="Weapon status">
        <span class="hud__section-tag">RIGHT ARM // BALLISTIC</span>
        <div class="hud__weapon-title">
          <strong data-ui="weapon-name">VX-9 RIFLE</strong>
          <span data-ui="weapon-status" data-state="ready">READY</span>
        </div>
        <div class="hud__ammo-row">
          <span class="hud__ammo" data-ui="weapon-ammo">36</span>
          <span class="hud__reserve" data-ui="weapon-reserve">/ 180</span>
          <span class="hud__ammo-label">ROUNDS</span>
        </div>
        <div class="hud__heat" data-ui="weapon-heat" role="progressbar" aria-label="Weapon heat" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><span></span></div>
        <div class="hud__cooldowns" aria-label="Weapon readiness">
          <span data-ui="rifle-cooldown" role="progressbar" aria-label="Rifle readiness" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100"><i></i><b>RF</b></span>
          <span data-ui="missile-cooldown" role="progressbar" aria-label="Missile readiness" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100"><i></i><b>MS</b></span>
          <span data-ui="blade-cooldown" role="progressbar" aria-label="Blade readiness" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100"><i></i><b>BL</b></span>
        </div>
        <div class="hud__ordnance"><span>MISSILE CELLS</span><strong data-ui="missile-count">16</strong></div>
      </section>

      <div class="hud__reticle" data-testid="reticle" aria-hidden="true">
        <div class="hud__lock-ring" data-testid="lock-ring"></div>
        <i class="hud__reticle-line hud__reticle-line--top"></i>
        <i class="hud__reticle-line hud__reticle-line--right"></i>
        <i class="hud__reticle-line hud__reticle-line--bottom"></i>
        <i class="hud__reticle-line hud__reticle-line--left"></i>
        <span class="hud__reticle-core"></span>
        <span class="hud__hitmark" data-ui="hit-marker" data-kind="hit"><i></i><i></i><i></i><i></i></span>
        <b data-ui="lock-label"></b>
      </div>

      <section class="hud__target hud-panel" data-testid="target-panel" aria-label="Target telemetry" hidden>
        <div class="hud__target-heading"><span class="hud__section-tag">TRACK // HOSTILE</span><b data-ui="target-threat">HOSTILE</b></div>
        <strong data-ui="target-name">UNKNOWN CONTACT</strong>
        <div class="hud__target-badges"><b class="hud__target-status" data-ui="target-status" hidden>ACS OVERLOAD // ×1.65</b><b class="hud__target-phase" data-ui="target-phase" hidden>PHASE 2 // CADENCE +</b></div>
        <div class="hud__target-data"><span data-ui="target-distance">000 m</span><span>AP <b data-ui="target-integrity">0 / 0</b></span></div>
        <div class="hud__target-track"><span data-ui="target-fill"></span></div>
        <div class="hud__target-data hud__target-data--stagger"><span>ACS LOAD</span><b data-ui="target-stagger">0 / 0</b></div>
        <div class="hud__target-track hud__target-track--stagger"><span data-ui="target-stagger-fill"></span></div>
      </section>

      <aside class="hud__controls" aria-label="Controls">
        <span><kbd>WASD</kbd> MOVE</span>
        <span><kbd>SHIFT</kbd> BOOST</span>
        <span><kbd>LMB</kbd> RIFLE</span>
        <span><kbd>RMB</kbd> MISSILE</span>
        <span><kbd>SPACE</kbd> VERTICAL</span>
        <span><kbd>Q / E</kbd> QUICK</span>
        <span><kbd>F</kbd> BLADE</span>
        <span><kbd>ESC</kbd> PAUSE</span>
      </aside>

      <div class="hud__toast" data-testid="toast" role="status" aria-live="polite" aria-atomic="true"></div>

      <section class="hud-overlay hud-start" data-testid="start-overlay" role="dialog" aria-modal="true" aria-labelledby="iron-veil-title">
        <div class="hud-start__scan" aria-hidden="true"></div>
        <div class="hud-start__rail hud-start__rail--left" aria-hidden="true"><span>07</span><small>ARES DIVISION</small></div>
        <div class="hud-start__panel">
          <p class="hud-start__eyebrow">COMBAT FRAME OPERATING SYSTEM <span>ONLINE</span></p>
          <h1 id="iron-veil-title"><span>IRON VEIL</span><small>// PROTOCOL</small></h1>
          <p class="hud-start__brief">Break the siege line. Isolate the command unit. Leave nothing transmitting.</p>
          <div class="hud-start__specs" aria-label="Mission briefing">
            <span><small>ZONE</small><strong>ASH MERIDIAN</strong></span>
            <span><small>THREAT</small><strong>SEVERE / 05</strong></span>
            <span><small>FRAME</small><strong>IV-07 HOUND</strong></span>
          </div>
          <button class="hud-button hud-start__button" data-testid="start-button" type="button">
            <span>DEPLOY FRAME</span><small>INITIALIZE AUDIO + CONTROL LINK</small>
          </button>
          <p class="hud-start__hint">Headphones recommended · Mouse capture begins on deployment</p>
        </div>
        <div class="hud-start__rail hud-start__rail--right" aria-hidden="true"><small>AUTH CODE</small><span>IVP-7734</span></div>
      </section>

      <section class="hud-overlay hud-pause" data-testid="pause-overlay" role="dialog" aria-modal="true" aria-labelledby="pause-title" hidden>
        <div class="hud-pause__panel">
          <span class="hud__section-tag">CONTROL LINK // STANDBY</span>
          <h2 id="pause-title">FRAME PAUSED</h2>
          <p>Combat simulation, projectiles, and mission time are frozen. Resume to recapture the control link.</p>
          <button class="hud-button" data-testid="resume-button" type="button"><span>RESUME LINK</span><small>RETURN TO COMBAT</small></button>
        </div>
      </section>

      <section class="hud-overlay hud-result" data-testid="result-overlay" role="dialog" aria-modal="true" aria-labelledby="mission-result-title" hidden>
        <div class="hud-result__panel">
          <span class="hud__section-tag" data-ui="result-kicker">PROTOCOL COMPLETE</span>
          <h2 id="mission-result-title" data-ui="result-title">VEIL SECURED</h2>
          <p data-ui="result-summary">All hostile command signatures have been neutralized.</p>
          <dl>
            <div><dt>COMBAT SCORE</dt><dd data-ui="result-score">0</dd></div>
            <div><dt>OPERATION TIME</dt><dd data-ui="result-time">00:00</dd></div>
          </dl>
          <button class="hud-button" data-testid="restart-button" type="button"><span>REDEPLOY</span><small>RESET COMBAT SIMULATION</small></button>
        </div>
      </section>
    `;
  }
}
